import type { IDBPDatabase } from 'idb'
import type {
  ProjectId,
  ProjectMetadata,
  Version,
  VersionId,
} from '@/domain/project/types'
import type { ProjectRepository } from '@/domain/project/ProjectRepository'
import type { ProjectKey } from '@/domain/block/projectKey'
import {
  deleteMarkfyDb,
  MARKFY_DB_NAME,
  openMarkfyDb,
  type MarkfyDb,
} from './openMarkfyDb'

/**
 * IndexedDB-backed implementation of `ProjectRepository`.
 *
 * The adapter holds an open `IDBPDatabase` reference for the lifetime
 * of the page. `wipeAll` closes the connection and deletes the DB;
 * subsequent calls reopen it on demand.
 */
export class IdbProjectRepository implements ProjectRepository {
  private dbPromise: Promise<IDBPDatabase<MarkfyDb>> | null = null
  private readonly dbName: string

  constructor(dbName: string = MARKFY_DB_NAME) {
    this.dbName = dbName
  }

  private db(): Promise<IDBPDatabase<MarkfyDb>> {
    if (this.dbPromise == null) {
      this.dbPromise = openMarkfyDb(this.dbName)
    }
    return this.dbPromise
  }

  // Projects -------------------------------------------------------------

  async listProjects(): Promise<ProjectMetadata[]> {
    const db = await this.db()
    const all = await db.getAll('projects')
    return all.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
  }

  async getProject(id: ProjectId): Promise<ProjectMetadata | null> {
    const db = await this.db()
    return (await db.get('projects', id)) ?? null
  }

  async saveProject(p: ProjectMetadata): Promise<void> {
    const db = await this.db()
    await db.put('projects', p)
  }

  async deleteProject(id: ProjectId): Promise<void> {
    const db = await this.db()
    const tx = db.transaction(
      ['projects', 'versions', 'projectSecrets'],
      'readwrite',
    )
    await tx.objectStore('projects').delete(id)
    const versionIds = await tx.objectStore('versions').index('projectId').getAllKeys(id)
    for (const vid of versionIds) {
      await tx.objectStore('versions').delete(vid)
    }
    await tx.objectStore('projectSecrets').delete(id)
    await tx.done
  }

  // Versions -------------------------------------------------------------

  async listVersions(projectId: ProjectId): Promise<Version[]> {
    const db = await this.db()
    const all = await db.getAllFromIndex('versions', 'projectId', projectId)
    return all.sort((a, b) => (a.importedAt < b.importedAt ? 1 : -1))
  }

  async getVersion(versionId: VersionId): Promise<Version | null> {
    const db = await this.db()
    return (await db.get('versions', versionId)) ?? null
  }

  async saveVersion(v: Version): Promise<void> {
    const db = await this.db()
    await db.put('versions', v)
  }

  async pruneOldVersions(projectId: ProjectId, keep: number): Promise<void> {
    const versions = await this.listVersions(projectId) // newest first
    const toDrop = versions.slice(keep)
    if (toDrop.length === 0) return
    const db = await this.db()
    const tx = db.transaction('versions', 'readwrite')
    for (const v of toDrop) await tx.store.delete(v.id)
    await tx.done
  }

  // Project secrets ------------------------------------------------------

  async getProjectKey(projectId: ProjectId): Promise<ProjectKey | null> {
    const db = await this.db()
    const row = await db.get('projectSecrets', projectId)
    return row?.key ?? null
  }

  async saveProjectKey(projectId: ProjectId, key: ProjectKey): Promise<void> {
    const db = await this.db()
    await db.put('projectSecrets', { projectId, key })
  }

  // Meta -----------------------------------------------------------------

  async getMeta<T = unknown>(key: string): Promise<T | null> {
    const db = await this.db()
    const row = await db.get('meta', key as never)
    return (row?.value as T | undefined) ?? null
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    const db = await this.db()
    await db.put('meta', { key: key as never, value })
  }

  // Wipe -----------------------------------------------------------------

  async wipeAll(): Promise<void> {
    if (this.dbPromise) {
      const db = await this.dbPromise
      db.close()
      this.dbPromise = null
    }
    await deleteMarkfyDb(this.dbName)
  }
}
